import Axios from 'axios';
import { api } from '@/shared/axios-client';
import { environment } from '@/environments/environment';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export type TestRunSpecSummary = {
  specId: string;
  scanId: string;
  name: string;
  createdAt: string;
  summary?: {
    methodCount?: number;
    haveTestsCount?: number;
    noTestsCount?: number;
    testsToRunCount?: number;
  };
  priorityLabels?: string[];
};

export type TestRunSpecSource = {
  type?: string;
  originEntityKey?: string;
  originEntityKeys?: string[];
  depth?: number;
  testScriptPath?: string;
  changeKind?: string;
};

export type TestRunSpecBatch = {
  batchNumber: number;
  testCaseId: string;
  testCaseName: string;
  methodNames: string[];
  entityKeys: string[];
  lastKnownStatus?: string;
  sources?: TestRunSpecSource[];
  testScriptPath?: string;
};

export type TestRunSpecPriorityGroup = {
  priority: string;
  label: string;
  batches: TestRunSpecBatch[];
};

export type TestRunSpecMethod = {
  entityKey: string;
  entityId?: string;
  name: string;
  rawName?: string;
  type?: string;
  filePath?: string;
  lineStart?: number;
  lineEnd?: number;
  priority?: string;
  priorityLabel?: string;
  coveredByTests?: { testCaseId: string; testCaseName: string }[];
  linkedTestCaseIds?: string[];
  sources?: TestRunSpecSource[];
};

export type TestRunSpecTestCase = {
  testCaseId: string;
  testCaseName?: string;
  priority?: string;
  priorityLabel?: string;
  lastKnownStatus?: string;
  sources?: TestRunSpecSource[];
  testScriptPath?: string;
};

export type TestRunSpecDetail = TestRunSpecSummary & {
  priorities: TestRunSpecPriorityGroup[];
  methodsWithoutTests: TestRunSpecMethod[];
  methods: TestRunSpecMethod[];
  testCases?: TestRunSpecTestCase[];
};

export type ListTestRunSpecsResponse = {
  success: boolean;
  scanId: string;
  total: number;
  specs: TestRunSpecSummary[];
};

export type GetTestRunSpecResponse = {
  success: boolean;
  spec: TestRunSpecDetail;
};

export const fetchTestRunSpecs = (scanId: string): Promise<ListTestRunSpecsResponse> =>
  api.get('/api/test-intelligence/test-run-specs', { params: { scanId: scanId.trim() } });

export const fetchTestRunSpec = (specId: string): Promise<GetTestRunSpecResponse> =>
  api.get(`/api/test-intelligence/test-run-spec/${specId}`);

export const deleteTestRunSpec = (
  specId: string,
): Promise<{ success: boolean; message?: string }> =>
  api.delete(`/api/test-intelligence/test-run-spec/${specId}`);

export const testRunSpecsQueryOptions = (scanId: string, enabled = true) =>
  queryOptions({
    queryKey: ['test-run-specs', scanId],
    queryFn: () => fetchTestRunSpecs(scanId),
    enabled: enabled && Boolean(scanId?.trim()),
  });

export const testRunSpecDetailQueryOptions = (specId: string | null, enabled = true) =>
  queryOptions({
    queryKey: ['test-run-spec', specId],
    queryFn: () => fetchTestRunSpec(specId!),
    enabled: enabled && Boolean(specId),
  });

export const useTestRunSpecs = (scanId: string, enabled = true) =>
  useQuery(testRunSpecsQueryOptions(scanId, enabled));

export const useTestRunSpecDetail = (specId: string | null, enabled = true) =>
  useQuery(testRunSpecDetailQueryOptions(specId, enabled));

export async function downloadTestRunSpecExcel(
  specId: string,
  fileName: string,
): Promise<void> {
  const res = await Axios.get(
    `/api/test-intelligence/test-run-spec/${specId}/download`,
    {
      baseURL: environment.apiUrl,
      responseType: 'blob',
    },
  );
  const blob = res.data as Blob;
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(objectUrl);
}

export function useDeleteTestRunSpec(scanId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteTestRunSpec,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['test-run-specs', scanId] });
    },
  });
}

export function formatSpecListMeta(spec: TestRunSpecSummary): string {
  const labels = (spec.priorityLabels ?? []).join(', ') || '—';
  const methods = spec.summary?.methodCount ?? 0;
  const noTests = spec.summary?.noTestsCount ?? 0;
  return `${labels} · ${methods} methods · ${noTests} no tests`;
}

export function formatSpecDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

